import React, { useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import useScrollReveal from "@/hooks/useScrollReveal";
import api from "@/lib/api";
import { toast } from "sonner";
import HeroBadge from "@/components/HeroBadge";
import { Phone, Mail, MapPin, Ambulance, FlaskConical, Baby, Sparkles, Send, MessageCircle, Facebook, Twitter } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", mobile: "", email: "", city: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  useScrollReveal();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/contact", form);
      toast.success("Thanks! Our team will reach out shortly.");
      setForm({ full_name: "", mobile: "", email: "", city: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setLoading(false); }
  };

  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-10 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>Contact · <span className="text-[#A53A2A]">Get In Touch</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              We'd love to <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">hear from you.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">
              Leave a message and a Nirmal Hospital advisor will respond within one business day. For emergencies, please call our 24×7 line.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid lg:grid-cols-12 gap-10">
        {/* Left — info cards */}
        <div className="lg:col-span-5 space-y-3 reveal">
          <ContactRow icon={Phone} label="Privilege Card Helpline · 24×7" lines={["+91-63570-33301"]} link="tel:+916357033301" accent />
          <ContactRow icon={Mail} label="Email us" lines={["npc@nirmalhospitals.in"]} link="mailto:npc@nirmalhospitals.in" />
          <ContactRow icon={MapPin} label="Visit us" lines={["Nirmal Super Speciality Hospital", "Subhash Chandra Bose Marg, Beside Hari Om Petrol Pump, Adajan, L. P. Savani, Surat — 395009, Gujarat"]} />

          <div className="flex items-center gap-3 pt-4">
            <a href="https://www.facebook.com/nirmalhospitals/" target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-full bg-white border border-[#E1E8F0] hover:bg-[#1E4F87] hover:text-white transition"><Facebook className="h-4 w-4" /></a>
            <a href="https://twitter.com/nirmalhospital1" target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-full bg-white border border-[#E1E8F0] hover:bg-[#1E4F87] hover:text-white transition"><Twitter className="h-4 w-4" /></a>
            <a href="https://wa.me/916357033301" target="_blank" rel="noreferrer" className="h-10 px-4 grid place-items-center rounded-full bg-[#1F7A3D] text-white font-semibold text-sm hover:bg-[#155F2D] transition"><MessageCircle className="h-4 w-4 inline mr-1.5" /> WhatsApp</a>
          </div>
        </div>

        {/* Right — form */}
        <form onSubmit={submit} className="lg:col-span-7 reveal lux-card rounded-3xl p-8 lg:p-10 space-y-4" data-testid="contact-form">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display text-2xl font-bold text-[#0F2540]">Send us a message</h2>
            <span className="text-xs text-[#5C6B7A]">Avg. response in 4–6 hours</span>
          </div>
          <FRow>
            <Inp label="Full Name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testid="c-name" />
            <Inp label="Mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} required testid="c-mobile" />
          </FRow>
          <FRow>
            <Inp label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="c-email" />
            <Inp label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="c-city" />
          </FRow>
          <Inp label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="c-subject" />
          <div>
            <label className="text-sm font-medium text-[#0F2540]">Message *</label>
            <textarea rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
              data-testid="c-message" />
          </div>
          <button disabled={loading} className="btn-primary w-full justify-center text-base py-3.5" data-testid="contact-submit-btn">
            {loading ? "Sending…" : <>Send message <Send className="h-4 w-4" /></>}
          </button>
        </form>
      </section>

      {/* MAP + Get Directions */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-16" data-testid="contact-map-section">
        <div className="reveal rounded-3xl overflow-hidden bg-white border border-[#E1E8F0] shadow-[0_24px_60px_-30px_rgba(15,37,64,0.18)]">
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-4 p-7 lg:p-9 bg-gradient-to-br from-[#0F2540] to-[#1E4F87] text-white flex flex-col">
              <span className="overline text-[#F5C6BE]">Find us</span>
              <h3 className="mt-3 font-display text-2xl font-bold leading-tight">Nirmal Super Speciality Hospital</h3>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">
                Subhash Chandra Bose Marg, Beside Hari Om Petrol Pump,<br />
                Adajan, L. P. Savani,<br />
                Surat — 395009, Gujarat, India
              </p>
              <div className="mt-6 flex flex-col gap-2.5 text-sm text-white/85">
                <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-[#F5C6BE]" /> +91-63570-33301</span>
                <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#F5C6BE]" /> 21.207°N · 72.785°E</span>
              </div>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Nirmal+Super+Speciality+Hospital+Adajan+Surat"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm text-[#0F2540] bg-white hover:bg-[#F5C6BE] transition-colors shadow-md"
                  data-testid="map-directions-btn"
                >
                  <MapPin className="h-4 w-4" /> Get directions
                </a>
                <a
                  href="https://www.google.com/maps/place/Nirmal+Super+Speciality+Hospital,+Subhash+Chandra+Bose+Marg,+L.+P.+Savani,+Adajan,+Surat,+Gujarat+395009"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm text-white border border-white/30 hover:bg-white/10 transition-colors"
                  data-testid="map-open-btn"
                >
                  Open in Maps
                </a>
              </div>
            </div>
            <div className="lg:col-span-8 relative min-h-[340px]">
              <iframe
                title="Nirmal Super Speciality Hospital — Adajan, Surat"
                src="https://www.google.com/maps?q=Nirmal+Super+Speciality+Hospital+Adajan+Surat&output=embed"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-map-iframe"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function ContactRow({ icon: Icon, label, lines, accent, link }) {
  return (
    <div className={`flex items-start gap-4 rounded-2xl p-5 border ${accent ? "bg-gradient-to-br from-[#A53A2A] to-[#7A2A1E] text-white border-[#A53A2A] shadow-xl" : "bg-white border-[#E1E8F0] hover:border-[#1E4F87] transition-colors"}`}>
      <div className={`h-11 w-11 rounded-xl grid place-items-center flex-shrink-0 ${accent ? "bg-white/15" : "bg-[#EAF1FA] text-[#1E4F87]"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className={`text-[10px] uppercase tracking-wider ${accent ? "text-white/85" : "text-[#5C6B7A]"}`}>{label}</div>
        <div className="space-y-0.5 mt-0.5">
          {lines.map((l, i) =>
            link ? <a key={i} href={link} className="block font-medium">{l}</a> : <div key={i} className="font-medium">{l}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FRow({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Inp({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-[#0F2540]">{label}{required && " *"}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
        data-testid={testid} />
    </div>
  );
}
